import xhrService from "../xhrService";
import { Address } from "./address";
import { Client, Contact } from "./client";
import db from '../../db/db';
import { withOfflineFallback } from '../offlineFallback';
const baseUrl = '/api/projects';

/**
 * Finish types
 *
 */
export type Project = {
  id: number;
  name: string;
  client_id: number;
  address_id: number;
  inspection_template_id: number;
  offline_projects?: any[];
  permittee?: string;
  notes?: string;
  timezone: string;
  document_group: number;
  start_date: string;
  end_date: string;
  inspection_compliance_required: boolean;
  cgp_tracking_id?: number;
  next_inspection_date: string;
  actual_award_date?: string;
  archivedAt?: string;
  status?: string;
  findingComplianceRequired: boolean;
  isCcrProject: boolean;
  address?: Address;
  client?: Client;
  inspector_user_id?: number;
  createdAt: Date;
  project_type_id?: Number;
  precipitation_flag?: boolean;
  precipitation_threshold?: number;
  snow_threshold?: number;
  external_project?: boolean;
  project_config?: string;
  dissolve_finding_images?: boolean;
  require_closure_photos?: boolean;
  enable_photo_metadata_requirement?: boolean;
  forecast_weather?: boolean;
  ca_inspection_scheduler?: boolean;
  current_acreage?: number;
  guest_sign_offs?: boolean;
  no_of_days_acknowledgment?: number;
  no_of_days_compliance?: number;
  primary_contact?: string | Array<{ name: string }>;
  project_inspection_templates?: Array<any>;
  doc_type_group_id?: number;
  esri_layers_enabled?: boolean;
};

/**
 * Go through types
 */
type ProjectApi = {
  create: (data: any) => Promise<Project>;
  get: (id: number) => Promise<Project>;
  getDocs: (id: number, params?: any, search?: any) => Promise<any>;
  getLog: (id: number) => Promise<any>;
  update: (id: number, data: any) => Promise<Project>;
  updateStatus: (id: number, status: string) => Promise<{ id: number; status: string }>;
  setInspectionDate: (id: number, data: { created_date: Date, timezone: string }) => Promise<any>;
  bulkStorm: {
    get: (projectIds: Array<number>) => Promise<any>;
    create: (data: any) => Promise<any>;
  };
  contacts: {
    index: (projectId: number) => Promise<Array<Contact>>;
    create: (projectId: number, userIds: Array<number>) => Promise<Contact>;
    destroy: (projectId: number, userId: number) => Promise<any>;
  };
  isPublicAvailable: (id: number, type: string) => Promise<any>;
  removeDuplicateLayers: (id: number) => Promise<any>;
  getProjectLegendItemLog: (id: number, legendItemId: number, showFull: boolean) => Promise<any>;
  getNotes: (id: number, showFull: boolean) => Promise<any>
  getProjectTypes: (tenantId: number) => Promise<any>
  updateInspectionIntervalId: (projectId: number, defaultIntervalId: any) => any;
  getHourlyPrecipitationData: (id: number, paramsObject: any) => Promise<any>;
  getDailyPrecipitationData: (id: number, paramsObject: any) => Promise<any>;
  updateOneHourlyPrecipitationEntry: (id: number, updatedData: any) => Promise<any>;
  updateOneDailyPrecipitationEntry: (id: number, updatedData: any) => Promise<any>;
  documentsLog: {
    getDocumentsLog: (projectId: number | null, filterType?: any, page?: any, rowsPerPage?: any, search?: any) => Promise<any>;
    restoreDocument: (projectId: number | null, projectUploadId: number | null, data: any) => Promise<any>;
    restoreSubfolder: (projectId: number | null, subFolderId: number | null, restoreDocuments: boolean, data: any) => Promise<any>;
  }
  getFindingsReport: (projectId: number | null, data: any) => Promise<any>;
  getPublicVisitReport: (projectId: number | null, data: any) => Promise<any>;
  getPrecipitationDataProjectWise: (projectId: number, date_range: any) => Promise<any>;
  getNOAAWeatherData: (id: number, paramsObject: any) => Promise<any>;
  refreshNOAAWeatherData: (id: number, paramsObject: any) => Promise<any>;
  sortDocs: (data: any, selectedSubDocType: any) => Promise<any>;
  fetchGuestProjectDetails: (uuid: string) => Promise<any>;
  getDocTypeByInspectionTemplateType: (projectId: number, inspectionTemplateTypeId: number) => Promise<any>;
  createDocType: (data: { project_id: number, inspection_template_type_id: number, doc_type_id: number }) => Promise<any>;
  saveGeoreference: (
    projectId: number,
    uploadId: number,
    data: {
      geo_top_left_lat: number;
      geo_top_left_lng: number;
      geo_top_right_lat: number;
      geo_top_right_lng: number;
      geo_bottom_left_lat: number;
      geo_bottom_left_lng: number;
      geo_bottom_right_lat: number;
      geo_bottom_right_lng: number;
      width?: number;
      height?: number;
    },
  ) => Promise<any>;
  createPendingSiteMap: (
    projectId: number,
    data: {
      project_doc_type_id: number;
      project_sub_doc_type_id?: number | null;
      site_map_type: string;
      // gis_esri / gis_image_esri carry an ArcGIS layer URL alongside the upload
      esri_url?: string | null;
    },
  ) => Promise<any>;
  linkPendingSiteMap: (
    projectId: number,
    projectUploadId: number,
    data: {
      upload_id: number;
      geo_top_left_lat: number;
      geo_top_left_lng: number;
      geo_top_right_lat: number;
      geo_top_right_lng: number;
      geo_bottom_left_lat: number;
      geo_bottom_left_lng: number;
      geo_bottom_right_lat: number;
      geo_bottom_right_lng: number;
    },
  ) => Promise<any>;
  esriLayer: {
    check: (projectId: number, url: string) => Promise<EsriCheckUrlResponse>;
    show: (projectId: number, uploadId: number) => Promise<any>;
    destroy: (projectId: number, uploadId: number) => Promise<void>;
  };
  esriPositionables: {
    list: (projectId: number, uploadId: number) => Promise<any[]>;
    refresh: (projectId: number, uploadId: number) => Promise<any[]>;
  };
  checkCoordinateOnSiteMaps: (
    projectId: number,
    inspectionId: number,
    lat: number,
    lng: number,
  ) => Promise<{
    hasCalibratedMaps: boolean;
    containingMaps: Array<{
      project_upload_id: number;
      name: string | null;
      site_map_type: string;
    }>;
  }>;
  /** Place a finding marker on the given georeferenced maps from its coordinate. */
  placeFindingOnMaps: (
    projectId: number,
    findingId: number,
    data: { lat: number; lng: number; project_upload_ids: number[] },
  ) => Promise<any[]>;
  notificationsUsersList: (projectId: number) => Promise<{
    /** Active inspection forms/templates for the project */
    forms: Array<{ id: number; name: string; is_primary: boolean }>;
    /** Project-level (all-forms / legacy) notification config */
    notificationTypes: Array<{
      id: number;
      name: string;
      is_enabled: boolean;
      users: Array<{ id: number; name: string; status: 'on' | 'off'; is_active: boolean }>;
    }>;
    /** Per-form notification config keyed by inspection_template_id */
    byForm: Record<number, Array<{
      id: number;
      name: string;
      is_enabled: boolean;
      users: Array<{ id: number; name: string; status: 'on' | 'off'; is_active: boolean }>;
    }>>;
  }>;
};

export type EsriCheckUrlResponse =
  | { valid: true; url: string; service_type: "FeatureServer" | "MapServer" }
  | { valid: false; error: string };

export const projectApi: ProjectApi = {
  create: data => {
    return xhrService.post(baseUrl, data).then(response => response.data);
  },
  get: async (id: number) => {
    return withOfflineFallback(
      () => xhrService.get(`${baseUrl}/${id}`).then((response) => response.data),
      () => db.projects.get(Number(id)),
    );
  },
  /**
   * @return {Promise<{docTypes: any[], regulations: any[], document_group_name: string}>}
   */
  getDocs: (id: number, doctype?: any, search?: any) => {
    const params: { doctype?: any } = {}
    const queryParams: { search?: any } = {};
    if (doctype) {
      params.doctype = doctype
    }

    if (search) {
      queryParams.search = search;
    }

    return xhrService
      .get(`${baseUrl}/${id}/docs?${new URLSearchParams(queryParams).toString()}`, { params })
      .then(response => response.data);
  },
  getLog: (id: number) => {
    return xhrService
      .get(`/api/changelog/projects/frontendChangelogs/${id}`)
      .then(response => response.data);
  },
  update: (id: number, data) => {
    return xhrService
      .put(`${baseUrl}/${id}`, data)
      .then(response => response.data)
      .catch(error => Promise.reject(error.response.data));
  },
  updateStatus: (id: number, status: string) => {
    return xhrService
      .patch(`${baseUrl}/${id}/status`, { status })
      .then(response => response.data);
  },
  /**
   * Change the due date of the next inspection
   *
   * @param {number} id Primary ket of project
   * @param {{ created_date: string }} data Form data
   */
  setInspectionDate: (id: number, data: { created_date: any, timezone: any }) => {
    let date;

    const dateParts = data.created_date.split('-');

    if (dateParts[0].length === 4) {
      // yyyy-mm-dd format
      // need to find a better way to handle this for now this is very rare case and this should do till we find a better way!
      date = dateParts[1] + '-' + dateParts[2] + '-' + dateParts[0];
    } else {
      // mm-dd-yyyy format
      date = data.created_date
    }

    return xhrService
      .put(`${baseUrl}/${id}/attr/next_inspection_date`, {
        value: date,
        timezone: data.timezone,
      })
      .then(response => response.data)
      .catch(error => Promise.reject(error.response.data));
  },
  bulkStorm: {
    get: (projectIds: Array<number>) => {
      return xhrService
        .get("/api/projects/post-storm", { params: { projectIds } })
        .then(res => res.data);
    },
    create: data => {
      return xhrService
        .post("/api/projects/post-storm", { data })
        .then(res => res.data);
    },
  },

  contacts: {
    index: (projectId: number) =>
      xhrService.get(`${baseUrl}/${projectId}/contacts`).then(res => res.data),
    create: (projectId: number, userIds: Array<number>) =>
      xhrService
        .post(`${baseUrl}/${projectId}/contacts`, { userIds })
        .then(res => res.data),
    destroy: (projectId: number, userId: number) =>
      xhrService
        .delete(`${baseUrl}/${projectId}/contacts/${userId}`)
        .then(res => res.data),
  },

  isPublicAvailable: (id: number, type: string) => {
    return xhrService
      .get(`${baseUrl}/isPublicAvailable`, {
        params: { id, type }
      })
      .then(response => response.data);
  },

  removeDuplicateLayers: (id: number) => {
    return xhrService.post(`${baseUrl}/${id}/removeDuplicateLayers`).then(response => response.data);
  },

  getProjectLegendItemLog: (id: number, legendItemId: number, showFull: boolean) => {
    return xhrService
      .get(`/api/changelog/projects/${id}/legendItem/${legendItemId}`, {
        params: {
          showFull: showFull
        }
      })
      .then(response => response.data);
  },

  getProjectTypes: () => {
    return xhrService
      .get(`/api/project-types?isIncluded=true`)
      .then(res => res.data);
  },

  getNotes: (id: number, showFull: boolean) => {
    return xhrService
      .get(`/api/changelog/projects/${id}/getNotes`, {
        params: {
          showFull: showFull
        }
      })
      .then(response => response.data);
  },

  updateInspectionIntervalId: (projectId: number, defaultIntervalId: number) => {
    return xhrService.put(`${baseUrl}/${projectId}/updateInspectionIntervalId`, { defaultIntervalId: defaultIntervalId }).then(res => res.data)
  },

  getHourlyPrecipitationData: (id: number, paramsObject: any) => {
    return xhrService.get(`${baseUrl}/${id}/hourlyPrecipitationLog`, {
      params: paramsObject
    }).then(response => {
      return response.data
    });
  },

  getDailyPrecipitationData: (id: number, paramsObject: any) => {
    return xhrService.get(`${baseUrl}/${id}/dailyPrecipitationLog`, {
      params: paramsObject
    }).then(response => {
      return response.data
    });
  },

  updateOneHourlyPrecipitationEntry: (projectId: number, updatedData: any) => {
    return xhrService.put(`${baseUrl}/${projectId}/updateOneHourlyPrecipitationEntry/${updatedData.id}`, { updatedData: updatedData }).then(res => res.data)
  },

  updateOneDailyPrecipitationEntry: (projectId: number, updatedData: any) => {
    return xhrService.put(`${baseUrl}/${projectId}/updateOneDailyPrecipitationEntry/${updatedData.id}`, { updatedData: updatedData }).then(res => res.data)
  },

  documentsLog: {
    getDocumentsLog: (projectId: number | null, filterType?: any, page?: any, rowsPerPage?: any, search?: any) => {
      let paramsObj = {
        p: page,
        rows: rowsPerPage,
        search,
        filter: filterType,
      };

      return xhrService
        .get(`${baseUrl}/${projectId}/getDocumentsLog`, {
          params: paramsObj,
        })
        .then((res) => {
          return res.data;
        });
    },
    restoreDocument: (projectId: number | null, projectUploadId: number | null, data: any) => {
      return xhrService.put(`${baseUrl}/${projectId}/documentsLog/restoreDocument/${projectUploadId}`, data).then(res => {
        return res.data
      })
    },
    restoreSubfolder: (projectId: number | null, subFolderId: number | null, restoreDocuments: boolean, data: any) => {
      data.restoreDocuments = restoreDocuments;
      return xhrService.put(`${baseUrl}/${projectId}/documentsLog/restoreSubfolder/${subFolderId}`, data).then(res => {
        return res.data
      })
    }
  },

  getFindingsReport: (projectId: number | null, data: any) => {
    return xhrService
      .get(`${baseUrl}/${projectId}/getFindingsReport`, {
        params: data
      })
      .then(response => response.data);
  },

  getPublicVisitReport: (projectId: number | null, data: any) => {
    return xhrService
      .get(`${baseUrl}/${projectId}/getPublicVisitReport`, {
        params: data
      })
      .then(response => response.data);
  },

  getPrecipitationDataProjectWise: (projectId: number, date_range: any) => {
    return xhrService.get(`${baseUrl}/${projectId}/getPrecipitationDataProjectWise?date_range[]=${date_range[0]}&date_range[]=${date_range[1]}`)
      .then(response => {
        return response.data
      });
  },

  getNOAAWeatherData: (id: number, paramsObject: any) => {
    return xhrService.get(`${baseUrl}/${id}/noaaWeatherTable`, {
      params: paramsObject
    }).then(response => {
      return response.data
    });
  },
  refreshNOAAWeatherData: (id: number, paramsObject: any) => {
     return xhrService.post(`${baseUrl}/${id}/refreshNOAAWeatherData`, paramsObject)
     .then(response => {
     return response.data
   });

  },

  sortDocs: (data: any, selectedSubDocType: any) => {
    return xhrService.post(`${baseUrl}/documents/sort`, { data, selectedSubDocType })
      .then(response => {
        return response.data
      });
  },

  fetchGuestProjectDetails: (uuid: string) => {
    return xhrService
      .get(`${baseUrl}/getGuestProjectData`, {
        params: {uuid}
      })
      .then(response => response.data);
  },

  getDocTypeByInspectionTemplateType: (projectId: number, inspectionTemplateTypeId: number) => {
    return xhrService
      .get(`${baseUrl}/${projectId}/doc-type/${inspectionTemplateTypeId}`)
      .then(response => response.data);
  },

  createDocType: (data: { project_id: number, inspection_template_type_id: number, doc_type_id: number }) => {
    return xhrService
      .post(`${baseUrl}/doc-type`, data)
      .then(response => response.data);
  },

  saveGeoreference: (
    projectId: number,
    uploadId: number,
    data: {
      geo_top_left_lat: number;
      geo_top_left_lng: number;
      geo_top_right_lat: number;
      geo_top_right_lng: number;
      geo_bottom_left_lat: number;
      geo_bottom_left_lng: number;
      geo_bottom_right_lat: number;
      geo_bottom_right_lng: number;
      width?: number;
      height?: number;
    },
  ) => {
    return xhrService
      .put(`${baseUrl}/${projectId}/map-upload/${uploadId}/georeference`, data)
      .then(response => response.data);
  },

  createPendingSiteMap: (projectId, data) => {
    return xhrService
      .post(`${baseUrl}/${projectId}/site-maps/pending`, data)
      .then(response => response.data);
  },

  linkPendingSiteMap: (projectId, projectUploadId, data) => {
    return xhrService
      .put(`${baseUrl}/${projectId}/site-maps/pending/${projectUploadId}/link`, data)
      .then(response => response.data);
  },

  esriLayer: {
    check: (projectId, url) =>
      xhrService
        .post(`${baseUrl}/${projectId}/uploads/pending/esri-layer/check`, { url })
        .then(r => r.data),
    show: (projectId, uploadId) =>
      xhrService
        .get(`${baseUrl}/${projectId}/uploads/${uploadId}/esri-layer`)
        .then(r => r.data),
    destroy: (projectId, uploadId) =>
      xhrService
        .delete(`${baseUrl}/${projectId}/uploads/${uploadId}/esri-layer`)
        .then(r => r.data),
  },

  esriPositionables: {
    list: (projectId, uploadId) =>
      xhrService
        .get(`${baseUrl}/${projectId}/uploads/${uploadId}/esri-positionables`)
        .then(r => r.data),
    refresh: (projectId, uploadId) =>
      xhrService
        .post(`${baseUrl}/${projectId}/uploads/${uploadId}/esri-positionables/refresh`)
        .then(r => r.data),
  },
  checkCoordinateOnSiteMaps: (projectId, inspectionId, lat, lng) => {
    return xhrService
      .get(`${baseUrl}/${projectId}/site-maps/coordinate-check`, {
        params: { inspectionId, lat, lng },
      })
      .then((response) => response.data);
  },

  placeFindingOnMaps: (projectId, findingId, data) => {
    return xhrService
      .post(`${baseUrl}/${projectId}/findings/${findingId}/place`, data)
      .then((response) => response.data);
  },

  notificationsUsersList: (projectId) => {
    return xhrService.get(`${baseUrl}/${projectId}/notifications-users-list`)
    .then(response => { return response.data })
  },
};

export default projectApi;
